"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const CONTACT_INFO = {
  address: "瑞聲助聽器到府服務團隊 (全台預約到府)",
  phone: "0800-555-705",
  email: "service@resound.com.tw",
  hours: "週一至週五 09:00-18:00 (預約制到府服務)"
};
const CITIES = ["台北市", "新北市", "桃園市", "台中市", "台南市", "高雄市", "基隆市", "新竹市", "嘉義市", "新竹縣", "苗栗縣", "宜蘭縣", "南投縣", "彰化縣", "雲林縣", "嘉義縣", "屏東縣", "花蓮縣", "台東縣", "澎湖縣", "金門縣", "連江縣"];
const CONTACT_TIMES = ["隨時皆可", "上午 09:00-12:00", "下午 13:00-17:00", "晚上 18:00-21:00"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="81327" className="flex h-full flex-col items-center justify-center space-y-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="flex size-20 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Send className="size-10" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">感謝您的預約！</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-sm px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          發送新訊息
        </Button>
      </div>;
  }
  const inputClasses = "rounded-sm bg-muted/50 border-border px-6 h-12 focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <h3 className="text-2xl font-bold text-foreground">聯絡諮詢服務</h3>
        <p className="mt-1 text-sm text-muted-foreground">請填寫以下表單，我們的專業聽力師將盡快與您聯繫安排時間。</p>
      </div>

      <FormField control={control} name="name" rules={{
      required: "請填寫您的真實姓名",
      minLength: {
        value: 2,
        message: "姓名至少需要 2 個字"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="ml-1 text-foreground/80">姓名</FormLabel>
            <FormControl>
              <Input placeholder="請輸入姓名" className={inputClasses} {...field} />
            </FormControl>
            <FormMessage className="ml-1" />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "請填寫聯絡電話",
        pattern: {
          value: /^09\d{8}$/,
          message: "請輸入有效的手機號碼 (09開頭)"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="ml-1 text-foreground/80">電話</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="09XXXXXXXX" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "請選擇居住地區"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="ml-1 text-foreground/80">居住地區</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={cn(inputClasses, "w-full")}>
                    <SelectValue placeholder="選擇您居住的縣市" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CITIES.map((city, i) => <SelectItem key={city} value={city} data-index={i}>
                      {city}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="ml-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="contact_time" render={({
      field
    }) => <FormItem>
            <FormLabel className="ml-1 text-foreground/80">方便聯絡時間</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className={cn(inputClasses, "w-full")}>
                  <SelectValue placeholder="選擇您方便的聯絡時間" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {CONTACT_TIMES.map((time, i) => <SelectItem key={time} value={time} data-index={i}>
                    {time}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage className="ml-1" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "請說明您的需求或問題",
      minLength: {
        value: 10,
        message: "說明內容至少需要 10 個字"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="ml-1 text-foreground/80">服務需求說明</FormLabel>
            <FormControl>
              <Textarea placeholder="請簡單描述您的聽力狀況或需要的服務（如：預約到府檢測、助聽器試戴等）..." className={cn("flex w-full resize-none py-4 text-base md:text-sm", "rounded-sm bg-muted/50 border-border px-6 min-h-[120px] focus-visible:bg-background focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:outline-none transition-all duration-300")} {...field} />
            </FormControl>
            <FormMessage className="ml-1" />
          </FormItem>} />

      {submitError && <div className="rounded-sm bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-sm bg-primary text-lg font-bold text-primary-foreground shadow-md transition-all duration-300 hover:bg-secondary hover:shadow-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            預約傳送中...
          </> : "立即送出預約"}
      </Button>
    </Form>;
}
export default function ContactRuishengHearingExperts() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="reservation-section" className="relative w-full bg-background py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={cn("overflow-hidden rounded-sm border border-border/50 bg-background shadow-xl transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
          <div className="grid grid-cols-1 lg:grid-cols-5">
            
            <div className="flex flex-col justify-center bg-primary p-10 lg:col-span-2 lg:p-16">
              <div className="mb-10">
                <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl">
                  到府服務，聽見幸福
                </h2>
                <p className="mt-4 text-lg text-primary-foreground/90 font-medium">
                  We come to you - 專業聽力師團隊全台到府服務，讓您在家也能享有五星級的聽力關懷。
                </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <Phone className="size-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-widest">免費服務專線</h4>
                    <p className="mt-1 text-4xl font-bold text-primary-foreground tracking-tighter">{CONTACT_INFO.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <MapPin className="size-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-widest">服務範圍</h4>
                    <p className="mt-1 text-lg font-medium text-primary-foreground">{CONTACT_INFO.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <Clock className="size-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-widest">預約諮詢時間</h4>
                    <p className="mt-1 text-base font-medium text-primary-foreground leading-relaxed">{CONTACT_INFO.hours}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <Mail className="size-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-widest">線上預約管道</h4>
                    <p className="mt-1 text-lg font-medium text-primary-foreground">Line / FB 搜尋：瑞聲助聽器</p>
                  </div>
                </div>
              </div>
            </div>

            
            <div className="bg-card p-10 lg:col-span-3 lg:p-16">
              <FormProvider formId="contact_form_home" sectionName="聯絡諮詢服務" defaultValues={{
              name: "",
              phone: "",
              email: "",
              contact_time: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="立即送出預約" successMessage="我們已收到您的預約需求，聽力專家將於營業時間內盡快與您聯繫。">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}